/**
 * La vue « Résultats » de la liste d'un examen national (lot 1, 01/11/2026).
 *
 * Même liste, mêmes filtres de classe et de recherche que la vue « Dossiers » :
 * seules les colonnes changent. Le résultat se saisit d'un clic sur la ligne,
 * comme la réception d'une pièce - c'est le geste qui se répète soixante fois
 * quand la liste officielle tombe. Le matricule national, le numéro de table
 * et la mention, plus rares, passent par la fenêtre de l'élève.
 */
import React, { useState } from 'react';
import { GraduationCap, Loader2, CheckCircle } from 'lucide-react';
import { CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/Label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '../ui/dialog';
import ChampSelect from '../ui/ChampSelect';

// Même largeur pour les deux boutons du pied (DESIGN-SYSTEM.md).
const BOUTON_PIED_CLASSES = 'h-9 min-w-[168px] justify-center gap-2';

const CLASSES_CHAMP = 'h-9 rounded-lg border-gray-200 focus:border-[#246990] focus:ring-2 focus:ring-[#246990]/15';

// Les teintes reprennent celles des badges de paiement du même écran : vert
// pour ce qui est acquis, rouge pour ce qui ne l'est pas.
const CHOIX_RESULTAT = [
  { valeur: 'admis', libelle: 'Admis', actif: 'bg-green-100 text-green-800' },
  { valeur: 'ajourne', libelle: 'Ajourné', actif: 'bg-red-100 text-red-800' },
  { valeur: 'absent', libelle: 'Absent', actif: 'bg-gray-200 text-gray-700' }
];

// Radix refuse une valeur vide : « aucun » tient la place de null.
const AUCUN = 'aucun';

const OPTIONS_RESULTAT = [
  { valeur: AUCUN, libelle: 'Non renseigné' },
  ...CHOIX_RESULTAT.map(({ valeur, libelle }) => ({ valeur, libelle }))
];

export const MENTIONS = [
  { valeur: 'passable', libelle: 'Passable' },
  { valeur: 'assez_bien', libelle: 'Assez bien' },
  { valeur: 'bien', libelle: 'Bien' },
  { valeur: 'tres_bien', libelle: 'Très bien' }
];

const OPTIONS_MENTION = [{ valeur: AUCUN, libelle: 'Aucune' }, ...MENTIONS];

export const libelleMention = (valeur) => MENTIONS.find((m) => m.valeur === valeur)?.libelle || null;

/** Le filtre de la vue « Résultats », à la place du filtre de statut du dossier. */
export const FILTRES_RESULTAT = [
  { valeur: 'all', libelle: 'Tous les résultats' },
  { valeur: 'admis', libelle: 'Admis' },
  { valeur: 'ajourne', libelle: 'Ajournés' },
  { valeur: 'absent', libelle: 'Absents' },
  { valeur: AUCUN, libelle: 'Non renseignés' }
];

/** Le résultat d'un élève tel que le filtre le compare. */
export const resultatDe = (parEleve, eleveId) => parEleve[eleveId]?.resultat || AUCUN;

const formaterTaux = (taux) => (taux === null || taux === undefined
  ? '-'
  : `${Number(taux).toLocaleString('fr-FR', { maximumFractionDigits: 1 })} %`);

const CarteChiffre = ({ libelle, valeur }) => (
  <div className="rounded-md border border-gray-200 bg-gray-50 p-3">
    <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">{libelle}</p>
    <p className="mt-1 text-lg font-semibold tabular-nums text-gray-900">{valeur}</p>
  </div>
);

/**
 * Les compteurs de tout l'examen, calculés par le serveur. Ils ne suivent pas
 * le filtre de classe : c'est le taux de l'école qu'on lit ici.
 */
export const SyntheseResultats = ({ synthese, libelleClasses }) => (
  <div className="mb-4">
    <p className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-gray-500">
      Tous les élèves de {libelleClasses}
    </p>
    <div className="grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-6">
      <CarteChiffre libelle="Candidats" valeur={synthese.eleves} />
      <CarteChiffre libelle="Admis" valeur={synthese.admis} />
      <CarteChiffre libelle="Ajournés" valeur={synthese.ajournes} />
      <CarteChiffre libelle="Absents" valeur={synthese.absents} />
      <CarteChiffre libelle="Non renseignés" valeur={synthese.non_renseignes} />
      {/* Le seul chiffre en gras fort : c'est lui que l'école retient. */}
      <div className="rounded-md border border-[#246990]/20 bg-[#eef7fb] p-3">
        <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">Taux de réussite</p>
        <p className="mt-1 text-2xl font-bold tabular-nums text-[#246990]">
          {formaterTaux(synthese.taux_reussite)}
        </p>
        <p className="text-[11px] text-gray-500">Admis sur présents</p>
      </div>
    </div>
  </div>
);

/** Admis / Ajourné / Absent, d'un clic. Recliquer le choix actif ne fait rien. */
const SelecteurResultat = ({ valeur, enCours, desactive, onChoisir }) => (
  <div className="inline-flex overflow-hidden rounded-lg border border-gray-200">
    {CHOIX_RESULTAT.map((choix, index) => {
      const actif = valeur === choix.valeur;
      return (
        <button
          key={choix.valeur}
          type="button"
          disabled={desactive || Boolean(enCours)}
          onClick={() => !actif && onChoisir(choix.valeur)}
          className={`inline-flex h-8 w-[78px] items-center justify-center text-xs font-semibold transition-colors disabled:cursor-not-allowed ${index > 0 ? 'border-l border-gray-200' : ''} ${actif
            ? choix.actif
            : `bg-white text-gray-500 ${desactive ? '' : 'hover:bg-gray-50'}`
            } ${enCours && enCours !== choix.valeur ? 'opacity-50' : ''}`}
        >
          {enCours === choix.valeur
            ? <Loader2 size={13} className="animate-spin" />
            : choix.libelle}
        </button>
      );
    })}
  </div>
);

/** Matricule national, numéro de table, résultat et mention d'un élève. */
const FenetreResultat = ({ eleve, resultat, libelleLong, onFermer, onEnregistrer }) => {
  const [formulaire, setFormulaire] = useState(() => ({
    matricule_national: resultat?.matricule_national || '',
    numero_table: resultat?.numero_table || '',
    resultat: resultat?.resultat || AUCUN,
    mention: resultat?.mention || AUCUN
  }));
  const [enCours, setEnCours] = useState(false);

  const changer = (cle, valeur) => setFormulaire((precedent) => ({ ...precedent, [cle]: valeur }));
  const admis = formulaire.resultat === 'admis';

  const enregistrer = async () => {
    setEnCours(true);
    try {
      await onEnregistrer({
        matricule_national: formulaire.matricule_national.trim() || null,
        numero_table: formulaire.numero_table.trim() || null,
        resultat: formulaire.resultat === AUCUN ? null : formulaire.resultat,
        mention: admis && formulaire.mention !== AUCUN ? formulaire.mention : null
      });
    } finally {
      setEnCours(false);
    }
  };

  return (
    <Dialog open onOpenChange={(ouvert) => !ouvert && !enCours && onFermer()}>
      <DialogContent className="w-[95vw] max-w-3xl !max-h-[92vh] overflow-hidden flex flex-col p-0 gap-0">
        <DialogHeader className="px-5 py-4 border-b">
          <DialogTitle className="flex items-center gap-3 text-lg font-semibold text-gray-900">
            <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-[#246990] text-white">
              <GraduationCap size={20} />
            </span>
            Résultat au {libelleLong}
          </DialogTitle>
          <DialogDescription className="text-sm text-gray-500">
            {eleve.nom} {eleve.prenom} - {eleve.classe_nom}
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto px-5 py-4">
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="matricule_national" className="text-sm text-gray-700">Matricule national</Label>
              <Input
                id="matricule_national"
                value={formulaire.matricule_national}
                onChange={(e) => changer('matricule_national', e.target.value)}
                placeholder="Celui des listes officielles"
                maxLength={60}
                className={CLASSES_CHAMP}
              />
              <p className="text-[11px] text-gray-500">
                Différent du matricule de l&apos;école{eleve.matricule ? ` (${eleve.matricule})` : ''}.
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="numero_table" className="text-sm text-gray-700">
                Numéro de table <span className="text-gray-400">(facultatif)</span>
              </Label>
              <Input
                id="numero_table"
                value={formulaire.numero_table}
                onChange={(e) => changer('numero_table', e.target.value)}
                maxLength={60}
                className={CLASSES_CHAMP}
              />
            </div>

            <div className="space-y-2">
              <Label className="text-sm text-gray-700">Résultat</Label>
              <ChampSelect
                valeur={formulaire.resultat}
                onChange={(valeur) => changer('resultat', valeur)}
                options={OPTIONS_RESULTAT}
                className="w-full"
              />
            </div>

            <div className="space-y-2">
              <Label className="text-sm text-gray-700">Mention</Label>
              <ChampSelect
                valeur={admis ? formulaire.mention : AUCUN}
                onChange={(valeur) => changer('mention', valeur)}
                options={OPTIONS_MENTION}
                desactive={!admis}
                className="w-full"
              />
              {!admis && (
                <p className="text-[11px] text-gray-500">Une mention ne s&apos;attribue qu&apos;à un admis.</p>
              )}
            </div>
          </div>
        </div>

        <DialogFooter className="px-5 py-4 border-t bg-gray-50 sm:justify-end gap-2">
          <Button variant="outline" onClick={onFermer} disabled={enCours} className={BOUTON_PIED_CLASSES}>
            Annuler
          </Button>
          <Button
            onClick={enregistrer}
            disabled={enCours}
            className={`${BOUTON_PIED_CLASSES} bg-[#246990] text-white hover:bg-[#1f5a7a] disabled:opacity-50`}
          >
            {enCours ? <Loader2 size={16} className="animate-spin" /> : <CheckCircle size={16} />}
            Enregistrer
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

/**
 * Le tableau des résultats, pour la page d'élèves déjà filtrée et découpée
 * par SectionExamen.
 *
 * `onEnregistrer(eleveId, champs)` lève une erreur portant le message du
 * serveur ; elle est rendue par `notifier`.
 */
export const TableauResultats = ({
  eleves,
  parEleve,
  peutSaisir,
  libelleLong,
  onEnregistrer,
  notifier
}) => {
  // { eleveId, valeur } : le bouton qui tourne est celui qu'on a cliqué.
  const [enCours, setEnCours] = useState(null);
  const [eleveOuvert, setEleveOuvert] = useState(null);

  const choisir = async (eleve, valeur) => {
    setEnCours({ eleveId: eleve.id, valeur });
    try {
      await onEnregistrer(eleve.id, { resultat: valeur });
    } catch (erreur) {
      notifier(erreur.message || "L'enregistrement a échoué", 'error');
    } finally {
      setEnCours(null);
    }
  };

  const enregistrerFenetre = async (champs) => {
    try {
      const reponse = await onEnregistrer(eleveOuvert.id, champs);
      notifier(reponse?.message || 'Résultat enregistré');
      setEleveOuvert(null);
    } catch (erreur) {
      notifier(erreur.message || "L'enregistrement a échoué", 'error');
    }
  };

  return (
    <CardContent className="p-0">
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-gray-50 border-b">
            <tr>
              <th className="px-4 py-3 text-left text-[11px] font-semibold uppercase tracking-wide text-gray-500">
                Élève
              </th>
              <th className="px-6 py-3 text-left text-[11px] font-semibold uppercase tracking-wide text-gray-500">
                Matricule national
              </th>
              <th className="px-6 py-3 text-center text-[11px] font-semibold uppercase tracking-wide text-gray-500">
                Résultat
              </th>
              <th className="px-6 py-3 text-center text-[11px] font-semibold uppercase tracking-wide text-gray-500">
                Mention
              </th>
              {peutSaisir && (
                <th className="w-[150px] px-6 py-3 text-center text-[11px] font-semibold uppercase tracking-wide text-gray-500">
                  Actions
                </th>
              )}
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {eleves.map((eleve) => {
              const resultat = parEleve[eleve.id] || null;
              const mention = libelleMention(resultat?.mention);
              return (
                <tr key={eleve.id} className="hover:bg-gray-50">
                  <td className="px-4 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="flex-shrink-0 h-10 w-10 rounded-full bg-gray-200 flex items-center justify-center">
                        {eleve.nom.charAt(0)}
                      </div>
                      <div className="ml-4">
                        <div className="font-medium text-gray-900">{eleve.nom} {eleve.prenom}</div>
                        <div className="text-sm text-gray-500">{eleve.matricule}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm">
                    {resultat?.matricule_national
                      ? <span className="font-medium tabular-nums text-gray-900">{resultat.matricule_national}</span>
                      : <span className="text-gray-400">-</span>}
                    {resultat?.numero_table && (
                      <p className="text-[11px] text-gray-500">Table {resultat.numero_table}</p>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-center">
                    <SelecteurResultat
                      valeur={resultat?.resultat || null}
                      enCours={enCours?.eleveId === eleve.id ? enCours.valeur : null}
                      desactive={!peutSaisir}
                      onChoisir={(valeur) => choisir(eleve, valeur)}
                    />
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-center text-sm">
                    {mention
                      ? <span className="text-gray-900">{mention}</span>
                      : <span className="text-gray-400">-</span>}
                  </td>
                  {peutSaisir && (
                    <td className="px-6 py-4 whitespace-nowrap text-center">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setEleveOuvert(eleve)}
                        className="border-[#246990] text-[#246990] hover:bg-[#246990] hover:text-white"
                      >
                        Modifier
                      </Button>
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {eleveOuvert && (
        <FenetreResultat
          key={eleveOuvert.id}
          eleve={eleveOuvert}
          resultat={parEleve[eleveOuvert.id] || null}
          libelleLong={libelleLong}
          onFermer={() => setEleveOuvert(null)}
          onEnregistrer={enregistrerFenetre}
        />
      )}
    </CardContent>
  );
};

export default TableauResultats;
